import React, { useEffect, useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import { useDashTheme } from '../../DashboardV2/hooks/useDashTheme';
import { SectionCard, Button } from '../../DashboardV2/components/primitives';
import { getMonthlyScores } from '../../../services/goalsPerformanceApi';

const PerformanceTrendsCard = ({ canToggleTeam = false }) => {
  const t = useDashTheme();
  const isDark = t.isDark;

  const [range, setRange] = useState('6m');
  const [scope, setScope] = useState('self');
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getMonthlyScores({ range, scope })
      .then(res => {
        if (cancelled) return;
        setPoints(res?.data?.points || []);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load trends');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range, scope]);

  const { categories, series } = useMemo(() => ({
    categories: points.map(p => p.monthShort || (p.month || '').slice(0, 3).toUpperCase()),
    series: [{ name: scope === 'team' ? 'Team avg score' : 'Your score', data: points.map(p => p.score) }]
  }), [points, scope]);

  const chartOptions = useMemo(() => ({
    chart: { toolbar: { show: false }, zoom: { enabled: false }, background: 'transparent' },
    theme: { mode: isDark ? 'dark' : 'light' },
    stroke: { curve: 'smooth', width: 2.5 },
    colors: [t.accents[0]],
    fill: {
      type: 'gradient',
      gradient: { shadeIntensity: 1, opacityFrom: 0.25, opacityTo: 0, stops: [0, 90, 100] }
    },
    dataLabels: { enabled: false },
    grid: { borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', strokeDashArray: 4 },
    xaxis: {
      categories,
      labels: { style: { colors: t.c.textSecondary, fontSize: '11px', fontWeight: 500 } },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: {
      min: 0, max: 10,
      labels: { show: false },
    },
    tooltip: {
      theme: isDark ? 'dark' : 'light',
      y: { formatter: (v) => `${v}/10` }
    },
    markers: { size: 4, strokeWidth: 0, hover: { size: 6 } }
  }), [categories, isDark, t]);

  const placeholder = (children) => (
    <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: t.font.size.base, color: t.c.textSecondary }}>
      {children}
    </div>
  );

  return (
    <SectionCard
      title="Performance Trends"
      subtitle={`Historical score progress over the last ${range === '1y' ? '12 months' : '6 months'}`}
      style={{ height: '100%' }}
      actions={
        <>
          {canToggleTeam && (
            <>
              <Button size="sm" variant="ghost" active={scope === 'self'} onClick={() => setScope('self')}>Self</Button>
              <Button size="sm" variant="ghost" active={scope === 'team'} onClick={() => setScope('team')}>Team</Button>
            </>
          )}
          <Button size="sm" variant="ghost" active={range === '6m'} onClick={() => setRange('6m')}>6M</Button>
          <Button size="sm" variant="ghost" active={range === '1y'} onClick={() => setRange('1y')}>1Y</Button>
        </>
      }
    >
      {loading ? (
        <div style={{ height: 220, borderRadius: t.r.md, background: t.c.surfaceMuted }} className="animate-pulse" />
      ) : error ? (
        placeholder(error)
      ) : points.length === 0 ? (
        placeholder('No score history yet — complete a cycle to see trends.')
      ) : (
        <Chart options={chartOptions} series={series} type="area" height={220} />
      )}
    </SectionCard>
  );
};

export default PerformanceTrendsCard;
